/**
 * GMA-821 — normalize multi-device Firebase presence for caregiver web UI.
 * Maps platform + device_model to iPhone / iPad / Apple Watch and battery tiers.
 */

export const DEVICE_KIND = Object.freeze({
  IPHONE: "iphone",
  IPAD: "ipad",
  WATCH: "watch",
  UNKNOWN: "unknown",
});

export const BATTERY_TIER = Object.freeze({
  FULL: "full",
  MID: "mid",
  LOW: "low",
  UNKNOWN: "unknown",
});

/** @typedef {"iphone"|"ipad"|"watch"|"unknown"} DeviceKind */
/** @typedef {"full"|"mid"|"low"|"unknown"} BatteryTier */

const ONLINE_STALE_MS = 10 * 60 * 1000; // match backend stale cleanup (~10 min)

/**
 * cortex-be Controllers/PresenceController.js writes the header-echo fields
 * UNCONDITIONALLY with a literal fallback:
 *
 *   x_app_version  = headers["x-app-version"]  || appVersion  || "unknown"
 *   x_build_number = headers["x-build-number"] || buildNumber || "unknown"
 *
 * so a device that never reported a build still persists the string "unknown".
 * A plain `a || b || c` chain returns that truthy string and the UI renders it
 * as though it were a real build. Absent must read as absent (#489) — QA acting
 * on a fabricated build number is the defect this guards.
 * @param {...unknown} values
 * @returns {string|null}
 */
function firstReportedValue(...values) {
  for (const value of values) {
    if (value === null || value === undefined) continue;
    const text = String(value).trim();
    if (text === "" || text.toLowerCase() === "unknown") continue;
    return text;
  }
  return null;
}

/**
 * @param {{ platform?: string, deviceModel?: string, deviceInfo?: string, device_model?: string, device_info?: string }} device
 * @returns {DeviceKind}
 */
export function resolveDeviceKind(device = {}) {
  const platform = String(
    device.platform || device.x_app_platform || "",
  ).toLowerCase();
  const model = String(
    device.deviceModel ||
      device.device_model ||
      device.deviceInfo ||
      device.device_info ||
      "",
  ).toLowerCase();

  if (platform === "apple_watch" || platform.includes("watch")) {
    return DEVICE_KIND.WATCH;
  }
  if (model.includes("ipad") || platform.includes("ipad")) {
    return DEVICE_KIND.IPAD;
  }
  if (
    platform.includes("ios") ||
    platform.includes("swiftui") ||
    platform.includes("mobile_ios") ||
    model.includes("iphone") ||
    model.includes("ipod")
  ) {
    return DEVICE_KIND.IPHONE;
  }
  return DEVICE_KIND.UNKNOWN;
}

/**
 * @param {number|null|undefined} level — 0–100 integer percent
 * @returns {BatteryTier}
 */
export function getBatteryTier(level) {
  if (level === null || Number.isNaN(Number(level))) {
    return BATTERY_TIER.UNKNOWN;
  }
  const n = Number(level);
  if (n < 20) return BATTERY_TIER.LOW;
  if (n < 50) return BATTERY_TIER.MID;
  return BATTERY_TIER.FULL;
}

/**
 * @param {Record<string, unknown>|null|undefined} rawDevice
 * @param {number} [nowMs]
 */
export function normalizePresenceDevice(rawDevice, nowMs = Date.now(), deviceId = null) {
  if (!rawDevice || typeof rawDevice !== "object") return null;

  // Use nullish coalescing so a legitimate 0 isn't treated as missing, and a
  // finite-number check so a non-numeric timestamp becomes null instead of
  // NaN — NaN would make `stale` always false and wrongly mark a device online.
  const lastSeenRaw = rawDevice.last_seen ?? rawDevice.updated_at ?? null;
  const lastSeenNum = Number(lastSeenRaw);
  const lastSeenMs =
    lastSeenRaw !== null && lastSeenRaw !== "" && Number.isFinite(lastSeenNum)
      ? lastSeenNum
      : null;
  const stale = lastSeenMs !== null && nowMs - lastSeenMs > ONLINE_STALE_MS;

  const isOnline = Boolean(rawDevice.is_online) && !stale;
  const batteryRaw =
    rawDevice.battery_level ?? rawDevice.batteryLevel ?? null;
  const batteryLevel =
    batteryRaw === null || batteryRaw === ""
      ? null
      : Math.round(Number(batteryRaw));

  const kind = resolveDeviceKind(rawDevice);
  const platform = String(
    rawDevice.platform || rawDevice.x_app_platform || "unknown",
  );

  return {
    // The Firebase map key. Two devices of the same kind are distinct rows, so
    // kind is not a usable identity (#489 review).
    deviceId,
    kind,
    platform,
    isOnline,
    stale,
    batteryLevel:
      batteryLevel !== null && !Number.isNaN(batteryLevel) ? batteryLevel : null,
    batteryTier: getBatteryTier(
      batteryLevel !== null && !Number.isNaN(batteryLevel) ? batteryLevel : null,
    ),
    appVersion: firstReportedValue(
      rawDevice.app_version,
      rawDevice.x_app_version,
      rawDevice.appVersion,
    ),
    buildNumber: firstReportedValue(
      rawDevice.build_number,
      rawDevice.x_build_number,
      rawDevice.buildNumber,
    ),
    osVersion: firstReportedValue(
      rawDevice.os_version,
      rawDevice.osVersion,
    ),
    deviceModel:
      rawDevice.device_model || rawDevice.deviceModel || null,
    lastSeenMs,
  };
}

/**
 * @param {Record<string, unknown>|null|undefined} devicesMap
 * @param {{ onlineOnly?: boolean, nowMs?: number }} [options]
 * @returns {NonNullable<ReturnType<typeof normalizePresenceDevice>>[]}
 */
export function parseClientDevices(devicesMap, options = {}) {
  const { onlineOnly = false, nowMs = Date.now() } = options;
  if (!devicesMap || typeof devicesMap !== "object") return [];

  const parsed = Object.entries(devicesMap)
    .map(([deviceId, d]) => normalizePresenceDevice(d, nowMs, deviceId))
    .filter(Boolean);

  const filtered = onlineOnly ? parsed.filter((d) => d.isOnline) : parsed;

  const order = {
    [DEVICE_KIND.WATCH]: 0,
    [DEVICE_KIND.IPHONE]: 1,
    [DEVICE_KIND.IPAD]: 2,
    [DEVICE_KIND.UNKNOWN]: 3,
  };

  return filtered.sort(
    (a, b) => (order[a.kind] ?? 9) - (order[b.kind] ?? 9),
  );
}

/**
 * Collapse multiple device rows to one icon per kind (newest online wins).
 * @param {ReturnType<typeof parseClientDevices>} devices
 */
export function collapseDevicesByKind(devices) {
  const byKind = new Map();
  for (const device of devices) {
    const existing = byKind.get(device.kind);
    if (!existing) {
      byKind.set(device.kind, device);
      continue;
    }
    if (device.isOnline && !existing.isOnline) {
      byKind.set(device.kind, device);
      continue;
    }
    if (
      device.isOnline === existing.isOnline &&
      (device.lastSeenMs || 0) > (existing.lastSeenMs || 0)
    ) {
      byKind.set(device.kind, device);
    }
  }
  const order = [DEVICE_KIND.WATCH, DEVICE_KIND.IPHONE, DEVICE_KIND.IPAD];
  return order
    .map((kind) => byKind.get(kind))
    .filter(Boolean)
    .concat(
      [...byKind.entries()]
        .filter(([kind]) => !order.includes(kind))
        .map(([, d]) => d),
    );
}

export function getDeviceKindLabel(kind) {
  switch (kind) {
    case DEVICE_KIND.WATCH:
      return "Apple Watch";
    case DEVICE_KIND.IPAD:
      return "iPad";
    case DEVICE_KIND.IPHONE:
      return "iPhone";
    default:
      return "Device";
  }
}

export function getBatteryTierColor(tier) {
  switch (tier) {
    case BATTERY_TIER.LOW:
      return "#e53935";
    case BATTERY_TIER.MID:
      return "#fbc02d";
    case BATTERY_TIER.FULL:
      return "#43a047";
    default:
      return "#9e9e9e";
  }
}
